/** UtilityAdmin → Utility: Formal Letters & Hall Tickets (PDF generators). */
import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FileText, Ticket, Loader2, Sparkles, FileSignature, Plus, X, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import DocumentsGenerator from "./utility/DocumentsGenerator";

function useClassSections() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/sections")
      .then(({ data }) => setRows((data.sections || data || []).filter((r) => r.class_name && r.section)))
      .catch(() => setRows([]));
  }, []);
  const classes = useMemo(
    () => [...new Set(rows.map((r) => r.class_name))],
    [rows],
  );
  const sectionsFor = (cls) => [...new Set(rows.filter((r) => r.class_name === cls).map((r) => r.section))].sort();
  return { classes, sectionsFor };
}

const openPdf = (blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

function FormalLetters() {
  const { classes, sectionsFor } = useClassSections();
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("bonafide");
  const [cls, setCls] = useState("");
  const [section, setSection] = useState("");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/utility/letters/types").then(({ data }) => setTypes(data.types || [])).catch(() => {}); }, []);
  useEffect(() => {
    setStudentId("");
    if (!cls || !section) { setStudents([]); return; }
    api.get("/utility/students", { params: { class_name: cls, section } })
      .then(({ data }) => setStudents(data.students || []))
      .catch(() => setStudents([]));
  }, [cls, section]);

  const generate = async () => {
    if (!studentId) return toast.error("Pick a student first.");
    setBusy(true);
    try {
      const res = await api.post("/utility/letters/generate", { type, student_id: studentId, reason }, { responseType: "blob" });
      openPdf(new Blob([res.data], { type: "application/pdf" }));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not generate the letter."); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="letters-tab">
      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Letter type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="letter-type"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`letter-type-${t.key}`}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">{types.find((t) => t.key === type)?.desc}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Class</Label>
            <Select value={cls} onValueChange={(v) => { setCls(v); setSection(""); }}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="letter-class"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Section</Label>
            <Select value={section} onValueChange={setSection} disabled={!cls}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="letter-section"><SelectValue placeholder="Section" /></SelectTrigger>
              <SelectContent>{sectionsFor(cls).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Student</Label>
          <Select value={studentId} onValueChange={setStudentId} disabled={!students.length}>
            <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="letter-student"><SelectValue placeholder={students.length ? "Select student" : "Pick class & section"} /></SelectTrigger>
            <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.roll_number}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Purpose / reason (optional)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. bank account opening" className="h-11 rounded-xl mt-1" data-testid="letter-reason" />
        </div>
        <Button onClick={generate} disabled={busy || !studentId} className="rounded-xl" data-testid="letter-generate">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} Generate PDF
        </Button>
      </div>
      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-6 flex flex-col items-center justify-center text-center text-slate-400 gap-2">
        <Sparkles className="h-8 w-8 text-emerald-400" />
        <p className="text-sm">The letter opens in a new tab as a print-ready PDF on the school letterhead, auto-filled from the student record.</p>
      </div>
    </div>
  );
}

function HallTickets() {
  const { classes, sectionsFor } = useClassSections();
  const [cls, setCls] = useState("");
  const [section, setSection] = useState("");
  const [examKey, setExamKey] = useState("MT1");
  const [columns, setColumns] = useState([]);
  const [baseSubjects, setBaseSubjects] = useState([]); // mandatory
  const [subjects, setSubjects] = useState([]);         // displayed (mandatory + extra)
  const [schedule, setSchedule] = useState({}); // { subject: { date, time } }
  const [oralSubjects, setOralSubjects] = useState([]); // admin-added oral subjects
  const [oralSchedule, setOralSchedule] = useState({}); // { subject: { date, start_time, end_time } }
  const [newOralSub, setNewOralSub] = useState("");
  const [oralFirst, setOralFirst] = useState(false);    // print Oral section before Written
  const [paperSize, setPaperSize] = useState("legal");
  const [examMonth, setExamMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [newSub, setNewSub] = useState("");
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [rosterCount, setRosterCount] = useState(null); // null = not loaded, number = count
  const [roster, setRoster] = useState([]);             // full roster for the picker
  const [pickMode, setPickMode] = useState("all");       // "all" | "some"
  const [pickedIds, setPickedIds] = useState([]);        // student.id values
  const [pickQuery, setPickQuery] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sats/overview", { params: { exam_key: "MT1" } })
      .then(({ data }) => setColumns(data.columns || []))
      .catch(() => setColumns([]));
  }, []);

  // Load the mandatory subjects for the chosen class so the admin can set a
  // date & time per subject before generating.
  useEffect(() => {
    if (!cls) { setBaseSubjects([]); setSubjects([]); setSchedule({}); setOralSubjects([]); setOralSchedule({}); return; }
    setLoadingSubs(true);
    api.get("/utility/hall-ticket-subjects", { params: { class_name: cls } })
      .then(({ data }) => {
        const subs = data.subjects || [];
        setBaseSubjects(subs);
        setSubjects(subs);
        setSchedule((prev) => Object.fromEntries(subs.map((s) => [s, prev[s] || { date: "", start_time: "", end_time: "" }])));
      })
      .catch(() => { setBaseSubjects([]); setSubjects([]); })
      .finally(() => setLoadingSubs(false));
  }, [cls]);

  // Preflight roster count for the chosen class/section so we can warn (and
  // block) before firing off a request that would 400 with an opaque error.
  useEffect(() => {
    if (!cls || !section) { setRosterCount(null); setRoster([]); setPickedIds([]); return; }
    let cancelled = false;
    api.get("/utility/students", { params: { class_name: cls, section } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = data.students || [];
        setRoster(rows);
        setRosterCount(rows.length);
        setPickedIds([]);       // reset selection when section changes
        setPickMode("all");
      })
      .catch(() => { if (!cancelled) { setRosterCount(null); setRoster([]); } });
    return () => { cancelled = true; };
  }, [cls, section]);

  const setField = (sub, field, value) =>
    setSchedule((p) => ({ ...p, [sub]: { ...(p[sub] || { date: "", start_time: "", end_time: "" }), [field]: value } }));
  const moveSubject = (sub, dir) =>
    setSubjects((s) => {
      const i = s.indexOf(sub); const j = i + dir;
      if (i < 0 || j < 0 || j >= s.length) return s;
      const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
    });

  // ----- Oral subjects (all admin-added) -----
  const setOralField = (sub, field, value) =>
    setOralSchedule((p) => ({ ...p, [sub]: { ...(p[sub] || { date: "", start_time: "", end_time: "" }), [field]: value } }));
  const moveOralSubject = (sub, dir) =>
    setOralSubjects((s) => {
      const i = s.indexOf(sub); const j = i + dir;
      if (i < 0 || j < 0 || j >= s.length) return s;
      const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
    });
  const addOralSubject = () => {
    const name = newOralSub.trim();
    if (!name) return;
    if (oralSubjects.some((s) => s.toLowerCase() === name.toLowerCase())) { toast.error("That oral subject is already listed."); return; }
    setOralSubjects((s) => [...s, name]);
    setOralSchedule((p) => ({ ...p, [name]: { date: "", start_time: "", end_time: "" } }));
    setNewOralSub("");
  };
  const removeOralSubject = (sub) => {
    setOralSubjects((s) => s.filter((x) => x !== sub));
    setOralSchedule((p) => { const n = { ...p }; delete n[sub]; return n; });
  };

  const addSubject = () => {
    const name = newSub.trim();
    if (!name) return;
    if (subjects.some((s) => s.toLowerCase() === name.toLowerCase())) { toast.error("That subject is already listed."); return; }
    setSubjects((s) => [...s, name]);
    setSchedule((p) => ({ ...p, [name]: { date: "", start_time: "", end_time: "" } }));
    setNewSub("");
  };

  const removeSubject = (sub) => {
    setSubjects((s) => s.filter((x) => x !== sub));
    setSchedule((p) => { const n = { ...p }; delete n[sub]; return n; });
  };

  const generate = async () => {
    if (!cls || !section) return toast.error("Pick class and section.");
    setBusy(true);
    try {
      const extra_subjects = subjects.filter((s) => !baseSubjects.includes(s));
      const student_ids = pickMode === "some" ? pickedIds : [];
      const res = await api.post(
        "/utility/hall-tickets",
        { class_name: cls, section, exam_key: examKey, schedule, paper_size: paperSize, extra_subjects, subject_order: subjects, exam_month: examMonth, oral_subjects: oralSubjects, oral_schedule: oralSchedule, oral_first: oralFirst, student_ids },
        { responseType: "blob" },
      );
      openPdf(new Blob([res.data], { type: "application/pdf" }));
    } catch (e) {
      const d = e.response?.data;
      let msg = "Could not generate hall tickets.";
      if (d instanceof Blob) { try { msg = JSON.parse(await d.text()).detail || msg; } catch {} }
      toast.error(apiErr(msg) || msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="hall-tickets-tab">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Class</Label>
            <Select value={cls} onValueChange={(v) => { setCls(v); setSection(""); }}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="hall-class"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Section</Label>
            <Select value={section} onValueChange={setSection} disabled={!cls}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="hall-section"><SelectValue placeholder="Section" /></SelectTrigger>
              <SelectContent>{sectionsFor(cls).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Exam</Label>
            <Select value={examKey} onValueChange={setExamKey}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="hall-exam"><SelectValue /></SelectTrigger>
              <SelectContent>{columns.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Month &amp; Year</Label>
            <Input
              type="month"
              value={examMonth}
              onChange={(e) => setExamMonth(e.target.value)}
              className="h-11 rounded-xl mt-1"
              data-testid="hall-exam-month"
              title="Printed on every hall ticket (e.g. October 2025)"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Paper size</Label>
            <Select value={paperSize} onValueChange={setPaperSize}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="hall-paper"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="legal" data-testid="hall-paper-legal">Legal (8.5 × 14 in)</SelectItem>
                <SelectItem value="a4" data-testid="hall-paper-a4">A4 (8.27 × 11.7 in)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-6 flex flex-col items-center justify-center text-center text-slate-400 gap-2">
        <Ticket className="h-8 w-8 text-sky-400" />
        <p className="text-sm">Generates one hall ticket per student — with photo, roll number and your subject list in your chosen order with dates &amp; start/end times. <b>Legal fits 3 tickets per sheet; A4 fits 2</b>. Add non-mandatory subjects if needed.</p>
      </div>

      <div className="space-y-4 lg:col-span-2">
        {/* Toggle: print Oral before Written (also swaps their order in the form). */}
        {cls && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid="hall-oral-first-row">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Section order</div>
              <div className="text-sm text-slate-600 dark:text-slate-300 truncate">
                {oralFirst ? "Oral prints first, then Written." : "Written prints first, then Oral."}
              </div>
            </div>
            <label className="flex items-center gap-2 shrink-0 cursor-pointer">
              <span className="text-xs text-slate-500 dark:text-slate-400">Oral first</span>
              <Switch checked={oralFirst} onCheckedChange={setOralFirst} data-testid="hall-oral-first-toggle" />
            </label>
          </div>
        )}

        {/* Who to print for — everyone in the section, or a picked subset. */}
        {cls && section && rosterCount > 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid="hall-picker">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Print for</div>
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {pickMode === "all"
                    ? <>All <b>{rosterCount}</b> students in {cls} · {section}</>
                    : pickedIds.length === 0
                      ? <span className="text-rose-500">Pick at least one student below</span>
                      : <>{pickedIds.length} of {rosterCount} student{pickedIds.length === 1 ? "" : "s"} selected</>}
                </div>
              </div>
              <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-0.5 text-xs">
                <button type="button" onClick={() => setPickMode("all")}
                  className={`px-3 py-1 rounded-full transition ${pickMode === "all" ? "bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white" : "text-slate-500"}`}
                  data-testid="hall-mode-all">All students</button>
                <button type="button" onClick={() => setPickMode("some")}
                  className={`px-3 py-1 rounded-full transition ${pickMode === "some" ? "bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white" : "text-slate-500"}`}
                  data-testid="hall-mode-some">Choose students</button>
              </div>
            </div>

            {pickMode === "some" && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={pickQuery}
                    onChange={(e) => setPickQuery(e.target.value)}
                    placeholder="Search by name, roll no. or student ID"
                    className="h-9 rounded-lg text-sm"
                    data-testid="hall-pick-search"
                  />
                  <Button type="button" size="sm" variant="outline" className="rounded-lg h-9 shrink-0"
                    onClick={() => {
                      const q = pickQuery.trim().toLowerCase();
                      const matches = roster.filter((s) => {
                        if (!q) return true;
                        return (s.name || "").toLowerCase().includes(q)
                          || String(s.roll_number || "").toLowerCase().includes(q)
                          || String(s.student_id || "").toLowerCase().includes(q);
                      }).map((s) => s.id);
                      setPickedIds(Array.from(new Set([...pickedIds, ...matches])));
                    }}
                    data-testid="hall-pick-add-filtered"
                    disabled={roster.length === 0}
                  >Select matches</Button>
                  {pickedIds.length > 0 && (
                    <Button type="button" size="sm" variant="ghost" className="rounded-lg h-9 shrink-0 text-slate-500"
                      onClick={() => setPickedIds([])} data-testid="hall-pick-clear">Clear</Button>
                  )}
                </div>
                <div className="max-h-56 overflow-auto rounded-lg border border-slate-100 dark:border-slate-700/60 divide-y divide-slate-100 dark:divide-slate-700/60">
                  {roster
                    .filter((s) => {
                      const q = pickQuery.trim().toLowerCase();
                      if (!q) return true;
                      return (s.name || "").toLowerCase().includes(q)
                        || String(s.roll_number || "").toLowerCase().includes(q)
                        || String(s.student_id || "").toLowerCase().includes(q);
                    })
                    .map((s) => {
                      const checked = pickedIds.includes(s.id);
                      return (
                        <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`hall-pick-row-${s.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setPickedIds((p) => e.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id))}
                            className="accent-sky-500"
                            data-testid={`hall-pick-check-${s.id}`}
                          />
                          <span className="tabular-nums text-slate-400 w-10 shrink-0">{s.roll_number || "–"}</span>
                          <span className="flex-1 truncate">{s.name}</span>
                          <span className="text-[10px] tracking-wide uppercase text-slate-400 shrink-0">{s.student_id}</span>
                        </label>
                      );
                    })}
                  {roster.length > 0 && roster.filter((s) => {
                    const q = pickQuery.trim().toLowerCase();
                    if (!q) return true;
                    return (s.name || "").toLowerCase().includes(q)
                      || String(s.roll_number || "").toLowerCase().includes(q)
                      || String(s.student_id || "").toLowerCase().includes(q);
                  }).length === 0 && (
                    <div className="px-3 py-6 text-center text-xs text-slate-400">No students match your search.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col space-y-4">
        {/* Per-subject order, date & start/end time — printed on every hall ticket. */}
        {cls && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid="hall-schedule" style={{ order: oralFirst ? 2 : 1 }}>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Written exam schedule (per subject)</Label>
              {loadingSubs && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            </div>
            {subjects.length === 0 && !loadingSubs && (
              <p className="text-xs text-slate-400">No mandatory subjects found for this class. Add one below.</p>
            )}
            <div className="space-y-2">
              {subjects.length > 0 && (
                <div className="hidden md:grid grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-2 text-[10px] uppercase tracking-wide text-slate-400 px-0.5">
                  <span className="w-12">Order</span><span>Subject</span><span className="w-[9.5rem]">Date</span>
                  <span className="w-[7rem]">Start</span><span className="w-[7rem]">End</span><span className="w-6" />
                </div>
              )}
              {subjects.map((sub, idx) => {
                const isExtra = !baseSubjects.includes(sub);
                return (
                  <div key={sub} className="flex flex-wrap md:grid md:grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-2" data-testid={`hall-sched-row-${sub}`}>
                    <span className="w-12 flex items-center gap-0.5">
                      <button type="button" onClick={() => moveSubject(sub, -1)} disabled={idx === 0}
                        className="p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800 disabled:opacity-25 disabled:hover:bg-transparent"
                        data-testid={`hall-move-up-${sub}`} title="Move up"><ChevronUp className="h-4 w-4" /></button>
                      <button type="button" onClick={() => moveSubject(sub, 1)} disabled={idx === subjects.length - 1}
                        className="p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800 disabled:opacity-25 disabled:hover:bg-transparent"
                        data-testid={`hall-move-down-${sub}`} title="Move down"><ChevronDown className="h-4 w-4" /></button>
                    </span>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate flex items-center gap-1 flex-1 min-w-[8rem]">
                      <span className="text-[10px] text-slate-400 w-4 tabular-nums">{idx + 1}.</span>
                      {sub}
                      {isExtra && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300">extra</span>}
                    </span>
                    <Input
                      type="date"
                      value={schedule[sub]?.date || ""}
                      onChange={(e) => setField(sub, "date", e.target.value)}
                      className="h-9 w-[9.5rem] rounded-lg text-sm"
                      data-testid={`hall-date-${sub}`}
                    />
                    <Input
                      type="time"
                      value={schedule[sub]?.start_time || ""}
                      onChange={(e) => setField(sub, "start_time", e.target.value)}
                      className="h-9 w-[7rem] rounded-lg text-sm"
                      title="Exam start time"
                      data-testid={`hall-start-${sub}`}
                    />
                    <Input
                      type="time"
                      value={schedule[sub]?.end_time || ""}
                      onChange={(e) => setField(sub, "end_time", e.target.value)}
                      className="h-9 w-[7rem] rounded-lg text-sm"
                      title="Exam end time"
                      data-testid={`hall-end-${sub}`}
                    />
                    {isExtra
                      ? <button onClick={() => removeSubject(sub)} className="text-slate-400 hover:text-rose-500 px-1" data-testid={`hall-remove-${sub}`} title="Remove subject"><X className="h-4 w-4" /></button>
                      : <button onClick={() => removeSubject(sub)} className="text-slate-400 hover:text-rose-500 px-1" data-testid={`hall-remove-${sub}`} title="Remove this subject from the hall ticket (mandatory subject – you can add it back)"><X className="h-4 w-4" /></button>}
                  </div>
                );
              })}
            </div>

            {/* Add a subject even if it isn't mandatory (hall-ticket only). */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
              <Input
                value={newSub}
                onChange={(e) => setNewSub(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubject(); } }}
                placeholder="Add a subject (e.g. Computer, Drawing)"
                className="h-9 rounded-lg text-sm"
                data-testid="hall-add-subject-input"
              />
              <Button type="button" size="sm" variant="outline" className="rounded-lg h-9 shrink-0" onClick={addSubject} data-testid="hall-add-subject-btn">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            {subjects.length > 0 && (
              <p className="text-[11px] text-slate-400 mt-2">Use the arrows to set the print order. Day is filled automatically from the date; Start/End print as "09:30 AM – 12:30 PM". Leave blank to print an empty cell.</p>
            )}
          </div>
        )}

        {/* Oral exam schedule — starts empty; admin adds oral subjects as needed. */}
        {cls && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid="hall-oral-schedule" style={{ order: oralFirst ? 1 : 2 }}>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Oral exam schedule (per subject)</Label>
            </div>
            {oralSubjects.length === 0 && (
              <p className="text-xs text-slate-400">No oral subjects yet. Add one below — the Oral table only prints if at least one subject is listed.</p>
            )}
            <div className="space-y-2">
              {oralSubjects.length > 0 && (
                <div className="hidden md:grid grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-2 text-[10px] uppercase tracking-wide text-slate-400 px-0.5">
                  <span className="w-12">Order</span><span>Subject</span><span className="w-[9.5rem]">Date</span>
                  <span className="w-[7rem]">Start</span><span className="w-[7rem]">End</span><span className="w-6" />
                </div>
              )}
              {oralSubjects.map((sub, idx) => (
                <div key={sub} className="flex flex-wrap md:grid md:grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-2" data-testid={`hall-oral-row-${sub}`}>
                  <span className="w-12 flex items-center gap-0.5">
                    <button type="button" onClick={() => moveOralSubject(sub, -1)} disabled={idx === 0}
                      className="p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800 disabled:opacity-25 disabled:hover:bg-transparent"
                      data-testid={`hall-oral-move-up-${sub}`} title="Move up"><ChevronUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => moveOralSubject(sub, 1)} disabled={idx === oralSubjects.length - 1}
                      className="p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800 disabled:opacity-25 disabled:hover:bg-transparent"
                      data-testid={`hall-oral-move-down-${sub}`} title="Move down"><ChevronDown className="h-4 w-4" /></button>
                  </span>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate flex items-center gap-1 flex-1 min-w-[8rem]">
                    <span className="text-[10px] text-slate-400 w-4 tabular-nums">{idx + 1}.</span>
                    {sub}
                    <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300">oral</span>
                  </span>
                  <Input
                    type="date"
                    value={oralSchedule[sub]?.date || ""}
                    onChange={(e) => setOralField(sub, "date", e.target.value)}
                    className="h-9 w-[9.5rem] rounded-lg text-sm"
                    data-testid={`hall-oral-date-${sub}`}
                  />
                  <Input
                    type="time"
                    value={oralSchedule[sub]?.start_time || ""}
                    onChange={(e) => setOralField(sub, "start_time", e.target.value)}
                    className="h-9 w-[7rem] rounded-lg text-sm"
                    title="Exam start time"
                    data-testid={`hall-oral-start-${sub}`}
                  />
                  <Input
                    type="time"
                    value={oralSchedule[sub]?.end_time || ""}
                    onChange={(e) => setOralField(sub, "end_time", e.target.value)}
                    className="h-9 w-[7rem] rounded-lg text-sm"
                    title="Exam end time"
                    data-testid={`hall-oral-end-${sub}`}
                  />
                  <button onClick={() => removeOralSubject(sub)} className="text-slate-400 hover:text-rose-500 px-1" data-testid={`hall-oral-remove-${sub}`} title="Remove subject"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
              <Input
                value={newOralSub}
                onChange={(e) => setNewOralSub(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOralSubject(); } }}
                placeholder="Add an oral subject (e.g. Rhymes, Recitation, Story-telling)"
                className="h-9 rounded-lg text-sm"
                data-testid="hall-oral-add-input"
              />
              <Button type="button" size="sm" variant="outline" className="rounded-lg h-9 shrink-0" onClick={addOralSubject} data-testid="hall-oral-add-btn">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        )}
        </div>

        {cls && section && rosterCount === 0 && (
          <div className="rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-200" data-testid="hall-no-students">
            No students are enrolled in <b>{cls} · {section}</b> yet — hall tickets can only be generated for a section with at least one student. Add students from the <b>Sections</b> module first.
          </div>
        )}

        <Button
          onClick={generate}
          disabled={busy || !cls || !section || rosterCount === 0 || (pickMode === "some" && pickedIds.length === 0)}
          className="rounded-xl"
          data-testid="hall-generate"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ticket className="h-4 w-4 mr-2" />}
          {rosterCount === 0
            ? "No students in this section"
            : pickMode === "some"
              ? (pickedIds.length === 0 ? "Pick students to continue" : `Generate ${pickedIds.length} Hall Ticket${pickedIds.length === 1 ? "" : "s"}`)
              : `Generate Hall Tickets${rosterCount ? ` (${rosterCount})` : ""}`}
        </Button>
      </div>
    </div>
  );
}

export default function UtilityTools() {
  return (
    <Layout>
      <div className="space-y-6" data-testid="utility-tools-page">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 flex items-center justify-center">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Utility</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Generate student documents, formal letters and exam hall tickets.</p>
          </div>
        </div>
        <Tabs defaultValue="documents" className="w-full">
          <TabsList className="bg-slate-100 dark:bg-slate-800/60" data-testid="utility-tools-tabs">
            <TabsTrigger value="documents" data-testid="tab-documents" className="gap-1.5"><FileSignature className="h-4 w-4" /> Documents</TabsTrigger>
            <TabsTrigger value="letters" data-testid="tab-letters" className="gap-1.5"><FileText className="h-4 w-4" /> Formal Letters</TabsTrigger>
            <TabsTrigger value="hall-tickets" data-testid="tab-hall-tickets" className="gap-1.5"><Ticket className="h-4 w-4" /> Hall Tickets</TabsTrigger>
          </TabsList>
          <TabsContent value="documents" className="mt-6"><DocumentsGenerator /></TabsContent>
          <TabsContent value="letters" className="mt-6"><FormalLetters /></TabsContent>
          <TabsContent value="hall-tickets" className="mt-6"><HallTickets /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
